import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Check, X, Loader2 } from 'lucide-react';
import { cn } from '../lib/utils';
import { Input } from './ui/input';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from './ui/command';
import {
  Popover,
  PopoverContent,
  PopoverAnchor,
} from './ui/popover';
import { userAPI } from '../Services/apiService';

/**
 * Autocomplete user selection component with direct input typing
 * Supports both local filtering (from users prop) and backend search (via API)
 * @param {Object} props
 * @param {Array} props.users - Array of user objects (used for initial list and when backend search is disabled)
 * @param {string} props.value - Selected user ID (as string)
 * @param {Function} props.onChange - Callback when selection changes
 * @param {string} props.placeholder - Placeholder text
 * @param {React.ReactNode} props.icon - Icon component to display
 * @param {string} props.className - Additional CSS classes
 * @param {boolean} props.disabled - Whether the component is disabled
 * @param {boolean} props.enableBackendSearch - Enable backend search via API (default: false)
 * @param {Object} props.filterOptions - Additional filter options for backend search (e.g., { include_vendors: true, role: 'employee' })
 */
const UserCombobox = ({ 
  users = [], 
  value, 
  onChange, 
  placeholder = "Type to search...",
  icon: Icon,
  className = "",
  disabled = false,
  enableBackendSearch = false,
  filterOptions = {},
  dropdownWidth
}) => {
  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [backendUsers, setBackendUsers] = useState([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [searchCache, setSearchCache] = useState({});
  const inputRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  const abortControllerRef = useRef(null);

  // Merge users from both sources for selected user lookup
  const getAllUsers = () => {
    if (!enableBackendSearch) return users;
    // Merge and deduplicate by ID
    const userMap = new Map();
    users.forEach(user => userMap.set(user.id, user));
    backendUsers.forEach(user => userMap.set(user.id, user));
    return Array.from(userMap.values());
  };
  const allUsersForLookup = getAllUsers();
  const selectedUser = allUsersForLookup.find((user) => String(user.id) === String(value));

  // Update display value when user is selected
  useEffect(() => {
    if (selectedUser && !open) {
      setSearchValue(selectedUser.full_name || selectedUser.email || '');
    }
  }, [selectedUser, open]);

  // Backend search function with debouncing and caching
  const fetchUsersFromBackend = useCallback(async (searchQuery = '') => {
    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Cancel previous request if any
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller
    abortControllerRef.current = new AbortController();

    // Check cache first
    const cacheKey = searchQuery.toLowerCase().trim();
    if (searchCache[cacheKey]) {
      setBackendUsers(searchCache[cacheKey]);
      return;
    }

    // Debounce the search - wait 300ms after user stops typing
    return new Promise((resolve) => {
      searchTimeoutRef.current = setTimeout(async () => {
        setIsLoadingUsers(true);
        try {
          const params = {
            search: searchQuery.trim() || undefined,
            limit: 50, // Top 50 results as requested
            include_vendors: filterOptions.include_vendors !== false, // Default to true
            ...filterOptions
          };

          // Remove undefined values
          Object.keys(params).forEach(key => params[key] === undefined && delete params[key]);

          const response = await userAPI.getUsers(params, { 
            signal: abortControllerRef.current.signal 
          });
          
          const fetchedUsers = response.data || [];
          
          // Update cache
          setSearchCache(prev => ({
            ...prev,
            [cacheKey]: fetchedUsers
          }));
          
          setBackendUsers(fetchedUsers);
          resolve(fetchedUsers);
        } catch (error) {
          if (error.name !== 'AbortError') {
            console.error('Failed to fetch users:', error);
            setBackendUsers([]);
          }
        } finally {
          setIsLoadingUsers(false);
        }
      }, 300);
    });
  }, [filterOptions, searchCache]);

  // Fetch initial users when backend search is enabled and modal opens
  useEffect(() => {
    if (enableBackendSearch && open) {
      // If search value exists, search immediately; otherwise load initial set
      if (searchValue.trim()) {
        fetchUsersFromBackend(searchValue);
      } else {
        // Load initial users without search query (first 50)
        fetchUsersFromBackend('');
      }
    }
  }, [enableBackendSearch, open]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Determine which users to use for filtering
  const usersToFilter = enableBackendSearch ? backendUsers : users;

  // Filter users based on search (only if backend search is disabled)
  const filteredUsers = enableBackendSearch 
    ? usersToFilter // Backend already filtered, no need to filter again
    : users.filter((user) => {
        if (!searchValue) return true;
        const searchLower = searchValue.toLowerCase();
        const fullName = (user.full_name || '').toLowerCase();
        const email = (user.email || '').toLowerCase();
        return fullName.includes(searchLower) || email.includes(searchLower);
      });

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setSearchValue(newValue);
    setOpen(true);
    
    // If user clears the input, clear the selection
    if (!newValue) {
      onChange('');
    }

    // If backend search is enabled, fetch users from API
    if (enableBackendSearch) {
      fetchUsersFromBackend(newValue);
    }
  };

  const handleSelectUser = (user) => {
    onChange(String(user.id));
    setSearchValue(user.full_name || user.email || '');
    setOpen(false);
    inputRef.current?.blur();
  };

  const handleClearSelection = () => {
    onChange('');
    setSearchValue('');
    setOpen(false);
    inputRef.current?.focus();
  };

  return (
    <div className={cn("relative", className)}>
      <Popover open={open && !disabled} onOpenChange={(isOpen) => !disabled && setOpen(isOpen)}>
        <PopoverAnchor asChild>
          <div className="relative">
            {Icon && (
              <Icon className={cn(
                "absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 pointer-events-none z-10",
                disabled ? "text-gray-300" : "text-gray-400"
              )} />
            )}
            <Input
              ref={inputRef}
              type="text"
              value={searchValue}
              onChange={handleInputChange}
              onFocus={() => !disabled && setOpen(true)}
              placeholder={placeholder}
              className={cn(
                "w-full pr-8",
                Icon && "pl-1",
                disabled && "bg-gray-100 cursor-not-allowed opacity-60"
              )}
              autoComplete="off"
              disabled={disabled}
            />
            {value && searchValue && !disabled && (
              <button
                type="button"
                onClick={handleClearSelection}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="h-4 w-4 text-gray-400" />
              </button>
            )}
          </div>
        </PopoverAnchor>
        {open && !disabled && (
          <PopoverContent 
            className="p-0" 
            align="start"
            onOpenAutoFocus={(e) => e.preventDefault()}
            style={{ width: dropdownWidth || 'var(--radix-popover-trigger-width)' }}
          >
            <Command shouldFilter={false}>
              <CommandList 
                className="max-h-[250px] overflow-y-auto overscroll-contain"
                onWheel={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  e.currentTarget.scrollTop += e.deltaY;
                }}
              >
                {isLoadingUsers ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
                    <span className="ml-2 text-sm text-gray-500">Searching...</span>
                  </div>
                ) : (
                  <>
                    <CommandEmpty>
                      {enableBackendSearch && searchValue.trim()
                        ? `No users found matching "${searchValue}"`
                        : 'No user found.'}
                    </CommandEmpty>
                    {filteredUsers.length > 0 && (
                      <CommandGroup>
                        {filteredUsers.map((user) => (
                          <CommandItem
                            key={user.id}
                            value={`${user.full_name} ${user.email} ${user.id}`}
                            onSelect={() => handleSelectUser(user)}
                            className="px-2"
                          >
                            <div className="flex items-center gap-2 w-full">
                              {String(value) === String(user.id) && (
                                <Check className="h-4 w-4 shrink-0 text-green-600" />
                              )}
                              {Icon && <Icon className="h-3.5 w-3.5 text-gray-500 shrink-0" />}
                              <div className="flex flex-col flex-1 min-w-0">
                                <span className="text-sm font-medium truncate">
                                  {user.full_name || user.email}
                                </span>
                                {user.full_name && (
                                  <span className="text-xs text-gray-500 truncate">{user.email}</span>
                                )}
                              </div>
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    )}
                  </>
                )}
              </CommandList>
            </Command>
          </PopoverContent>
        )}
      </Popover>
    </div>
  );
};

export default UserCombobox;

